/**
 * TenantContext — Multi-tenant operational context for PeToTa Business.
 *
 * Authority: ADR-101 (Multi-Tenant RLS) + ADR-100 (BusinessProfileRegistry).
 *
 * This context holds the *client-side shadow* of the 7-layer hierarchy:
 *   organization → legal_entity → branch → vertical_capability
 *
 * The authoritative copy lives in the database (row-level security enforces
 * isolation). The frontend merely transmits the resolved tenant ID as
 * `X-Tenant-Id` / `X-Branch-Id` headers — the backend MUST revalidate.
 *
 * NEVER trust this context for authorization. It is for UX routing only.
 *
 * @see ADR-101 for tenant resolution flow
 * @see ADR-100 for BusinessProfile semantics
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface TenantMembership {
  organizationId: string;
  organizationName: string;
  legalEntityId: string;
  legalEntityName: string;
  branchId: string;
  branchName: string;
  /** BusinessProfile key controlling module set + pricing + scheduling engine. */
  businessProfileKey: string;
  /** Which sub-verticals this branch is permitted to operate. */
  verticalCapabilities: string[];
  /** User's role in this branch — drives module visibility, not authorization. */
  role: 'owner' | 'manager' | 'receptionist' | 'staff' | 'finance' | 'readonly';
}

export interface TenantContextValue {
  current: TenantMembership | null;
  memberships: TenantMembership[];
  isResolving: boolean;
  switchBranch: (branchId: string) => Promise<void>;
  clear: () => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

const STORAGE_KEY = 'petota-business:active-branch';

export function TenantProvider({
  children,
  initialMemberships,
  onSwitch,
}: {
  children: ReactNode;
  initialMemberships?: TenantMembership[];
  onSwitch?: (branchId: string) => Promise<TenantMembership[]>;
}): JSX.Element {
  const [memberships, setMemberships] = useState<TenantMembership[]>(
    initialMemberships ?? [],
  );
  const [currentBranchId, setCurrentBranchId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    if (initialMemberships) {
      setMemberships(initialMemberships);
    }
  }, [initialMemberships]);

  const current = useMemo(() => {
    if (memberships.length === 0) return null;
    if (currentBranchId) {
      const found = memberships.find((m) => m.branchId === currentBranchId);
      if (found) return found;
    }
    return memberships[0] ?? null;
  }, [memberships, currentBranchId]);

  const switchBranch = useCallback(
    async (branchId: string) => {
      if (!memberships.some((m) => m.branchId === branchId)) {
        throw new Error(`Branch ${branchId} is not in user's memberships`);
      }
      setIsResolving(true);
      try {
        if (onSwitch) {
          const refreshed = await onSwitch(branchId);
          setMemberships(refreshed);
        }
        setCurrentBranchId(branchId);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(STORAGE_KEY, branchId);
        }
      } finally {
        setIsResolving(false);
      }
    },
    [memberships, onSwitch],
  );

  const clear = useCallback(() => {
    setMemberships([]);
    setCurrentBranchId(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const value = useMemo<TenantContextValue>(
    () => ({ current, memberships, isResolving, switchBranch, clear }),
    [current, memberships, isResolving, switchBranch, clear],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within <TenantProvider>');
  }
  return ctx;
}

export function useCurrentBranch(): TenantMembership | null {
  return useTenant().current;
}
